import { Box, Container, Divider, Grid, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'

import { AppLogo } from '@/common/components'

import { TECHNICAL_LABELS } from '../constants/formConstants'

// Match the TechnicalBackground styling from TbxLoadingPage
const TechnicalBackground = styled(Box)(({ theme }) => ({
    background: theme.palette.background.default,
    minHeight: 'calc(100vh - 112px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    position: 'relative',
}))

// Match the TechnicalContainer styling from TbxLoadingPage
const TechnicalContainer = styled(Container)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: '8px',
    padding: theme.spacing(4),
    '&::after': {
        content: `"${TECHNICAL_LABELS.DRAWING_REFERENCE}"`,
        position: 'absolute',
        bottom: -12,
        right: 24,
        fontSize: '12px',
        color: theme.palette.text.secondary,
        background: theme.palette.background.paper,
        padding: '2px 8px',
        border: `1px solid ${theme.palette.action.disabled}`,
        borderRadius: '4px',
    },
}))

// Match the TechnicalContentContainer styling from SignOut
const TechnicalContentContainer = styled(Grid)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.active}`,
    padding: theme.spacing(4),
    borderRadius: '8px',
}))

// Match the TechnicalTypography styling from TbxLoadingPage
const TechnicalTypography = styled(Typography)({
    letterSpacing: '0.5px',
})

// Technical divider that matches the aesthetic
const TechnicalDivider = styled(Divider)(({ theme }) => ({
    height: 'auto',
    width: '1px',
    border: `1px dashed ${theme.palette.action.disabled}`,
    backgroundColor: 'transparent',
    margin: theme.spacing(0, 3),
    alignSelf: 'stretch',
}))

/**
 * OrganizationFormLayout - Technical blueprint-style layout for organization setup
 *
 * Provides a consistent technical aesthetic matching other Tempus Tools forms with:
 * - Blueprint-style borders and overlays
 * - Technical drawing reference labels
 * - Responsive two-column layout with technical dividers
 *
 * @param {Object} props
 * @param {Object} props.theme - MUI theme object
 * @param {React.ReactNode} props.leftColumn - Content for left column (account details)
 * @param {React.ReactNode} props.rightColumn - Content for right column (settings)
 * @param {React.ReactNode} props.actionButton - Action button component
 */
export const OrganizationFormLayout = ({ actionButton, leftColumn, rightColumn, theme }) => {
    return (
        <TechnicalBackground>
            <TechnicalContainer maxWidth="lg">
                {/* Logo Header */}
                <Stack
                    alignItems="center"
                    direction="row"
                    justifyContent="center"
                    mb={4}
                >
                    <AppLogo />
                </Stack>

                {/* Technical Content Section */}
                <TechnicalContentContainer
                    columnSpacing={4}
                    maxWidth="md"
                    container
                >
                    {/* Header Row */}
                    <Grid
                        mb={3}
                        size={12}
                    >
                        <Stack
                            alignItems="center"
                            direction="row"
                            justifyContent="space-between"
                            mb={2}
                        >
                            <TechnicalTypography
                                color={theme.palette.text.secondary}
                                sx={{ textTransform: 'uppercase' }}
                                variant="strong1"
                            >
                                {TECHNICAL_LABELS.HEADER_SETUP}
                            </TechnicalTypography>
                            <TechnicalTypography
                                color={theme.palette.text.secondary}
                                sx={{ textTransform: 'uppercase' }}
                                variant="strong1"
                            >
                                {TECHNICAL_LABELS.HEADER_WELCOME}
                            </TechnicalTypography>
                        </Stack>
                    </Grid>

                    {/* Left Column - Account Details */}
                    <Grid
                        size={{ xs: 12, md: 5.5 }}
                        spacing={2}
                    >
                        {leftColumn}
                    </Grid>

                    {/* Technical Divider (Desktop) */}
                    <Grid
                        size={1}
                        sx={{ display: { xs: 'none', md: 'flex' } }}
                    >
                        <TechnicalDivider orientation="vertical" />
                    </Grid>

                    {/* Mobile Divider */}
                    <Grid sx={{ display: { xs: 'block', md: 'none' } }}>
                        <Divider
                            sx={{
                                border: `1px dashed ${theme.palette.action.disabled}`,
                                backgroundColor: 'transparent',
                                my: 2,
                            }}
                        />
                    </Grid>

                    {/* Right Column - Organization Settings */}
                    <Grid size={{ xs: 12, md: 5.5 }}>{rightColumn}</Grid>

                    {/* Action Button Row */}
                    <Grid
                        mt={4}
                        size={12}
                        textAlign="right"
                    >
                        {actionButton}
                    </Grid>
                </TechnicalContentContainer>
            </TechnicalContainer>
        </TechnicalBackground>
    )
}

OrganizationFormLayout.propTypes = {
    actionButton: PropTypes.node.isRequired,
    leftColumn: PropTypes.node.isRequired,
    rightColumn: PropTypes.node.isRequired,
    theme: PropTypes.object.isRequired,
}
